import { useEffect, useState } from 'react';
import MD5 from 'crypto-js/md5';
import { checkUserBySession, getStoredSession } from '../../../domain/services/authService';
import {
    archiveStudentAccount,
    changeStudentPassword,
    createStudentAccount,
    deleteStudentAccount,
    fetchStudentsByOwner,
    unarchiveStudentAccount
} from '../../../domain/services/studentsService';

const PASSWORD_SALT = 'shineonyoucrazydiamond1975';

const EMPTY_FORM = {
    name: '', sname: '', ot: '', email: '', phone: '', sphone: '',
    city: '', cityW: '', bday: '', about: '', char: ''
};

export const useStudentsPage = () => {
    const [user, setUser] = useState(null);
    const [students, setStudents] = useState([]);
    const [archivedStudents, setArchivedStudents] = useState([]);
    const [formData, setFormData] = useState(EMPTY_FORM);
    const [loading, setLoading] = useState(false);
    const [copiedField, setCopiedField] = useState(null);
    const [showArchiveModal, setShowArchiveModal] = useState(false);
    const [studentToArchive, setStudentToArchive] = useState(null);
    const [passwordInputs, setPasswordInputs] = useState({});
    const [passwordLoadingId, setPasswordLoadingId] = useState(null);

    const fetchStudents = async () => {
        const { clid } = getStoredSession();
        if (!clid) return;

        try {
            const { activeStudents, archivedStudents: archived } = await fetchStudentsByOwner(clid);
            setStudents(activeStudents);
            setArchivedStudents(archived);
        } catch (error) {
            console.error('Fetch students error:', error);
        }
    };

    useEffect(() => {
        const checkAuth = async () => {
            try {
                const currentUser = await checkUserBySession();
                if (currentUser) setUser(currentUser);
            } catch (error) {
                console.error('Auth error:', error);
            }
        };
        checkAuth();
    }, []);

    useEffect(() => {
        if (user?.role !== 'student') fetchStudents();
    }, [user]);

    const createStudent = async () => {
        if (!formData.name || !formData.sname) {
            alert('Имя и фамилия обязательны');
            return;
        }

        const { clid } = getStoredSession();
        if (!clid) return;

        setLoading(true);
        try {
            const response = await createStudentAccount(clid, formData);
            if (response.data.ok) {
                setFormData(EMPTY_FORM);
                fetchStudents();
            } else {
                alert(`Произошла ошибка\nКод ошибки: ${response.data.type}`);
            }
        } catch (error) {
            alert('Ошибка сети');
            console.error('Create student error:', error);
        } finally {
            setLoading(false);
        }
    };

    const deleteStudent = async (studentId, studentName) => {
        if (!window.confirm(`Удалить аккаунт ${studentName}?`)) return;

        const { clid } = getStoredSession();
        if (!clid) return;

        try {
            const response = await deleteStudentAccount(clid, studentId);
            if (response.data.ok) fetchStudents();
            else alert('Аккаунт не был удален');
        } catch (error) {
            alert('Ошибка при удалении');
            console.error('Delete error:', error);
        }
    };

    const archiveStudent = (studentId, studentName) => {
        setStudentToArchive({ id: studentId, name: studentName });
        setShowArchiveModal(true);
    };

    const confirmArchive = async () => {
        if (!studentToArchive) return;

        const { clid, key } = getStoredSession();
        if (!clid || !key) return;

        try {
            const response = await archiveStudentAccount(clid, key, studentToArchive.id);
            if (response?.data?.ok) fetchStudents();
            else alert(`Ошибка при архивации: ${response?.data?.type || 'неизвестная ошибка'}`);
        } catch (error) {
            alert('Ошибка при архивации');
            console.error('Archive error:', error);
        } finally {
            setShowArchiveModal(false);
            setStudentToArchive(null);
        }
    };

    const unarchiveStudent = async (studentId, studentName) => {
        if (!window.confirm(`Вернуть ученицу ${studentName} из архива?`)) return;

        const { clid, key } = getStoredSession();
        if (!clid || !key) return;

        try {
            const response = await unarchiveStudentAccount(clid, key, studentId);
            if (response?.data?.ok) fetchStudents();
            else alert(`Ошибка при разархивации: ${response?.data?.type || 'неизвестная ошибка'}`);
        } catch (error) {
            alert('Ошибка при разархивации');
            console.error('Unarchive error:', error);
        }
    };

    const updatePasswordInput = (studentId, value) => {
        setPasswordInputs(prev => ({ ...prev, [studentId]: value }));
    };

    const changePassword = async (studentId, studentName) => {
        if (user?.role !== 'admin') {
            alert('Смена пароля доступна только администратору');
            return;
        }

        const newPassword = (passwordInputs[studentId] || '').trim();
        if (newPassword.length < 4) {
            alert('Пароль должен содержать не менее 4 символов');
            return;
        }

        const { clid, key } = getStoredSession();
        if (!clid || !key) {
            alert('Сессия истекла, войдите заново');
            return;
        }

        setPasswordLoadingId(studentId);
        try {
            const hashedPassword = MD5(newPassword + PASSWORD_SALT).toString();
            const response = await changeStudentPassword(clid, key, studentId, hashedPassword);

            if (response.data?.ok) {
                alert(`Пароль ученицы ${studentName} успешно изменен`);
                updatePasswordInput(studentId, '');
            } else {
                alert(`Не удалось изменить пароль: ${response.data?.type || 'неизвестная ошибка'}`);
            }
        } catch (error) {
            alert('Ошибка сети при изменении пароля');
            console.error('Change student password error:', error);
        } finally {
            setPasswordLoadingId(null);
        }
    };

    const copyToClipboard = async (text, field) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopiedField(field);
            setTimeout(() => setCopiedField(null), 2000);
        } catch (err) {
            alert('Ошибка копирования');
        }
    };

    const updateField = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    return {
        user,
        students,
        archivedStudents,
        formData,
        loading,
        copiedField,
        showArchiveModal,
        studentToArchive,
        passwordInputs,
        passwordLoadingId,
        actions: {
            createStudent,
            deleteStudent,
            archiveStudent,
            confirmArchive,
            unarchiveStudent,
            changePassword,
            copyToClipboard,
            updateField,
            updatePasswordInput,
            setShowArchiveModal
        }
    };
};
